/* eslint-disable react-hooks/rules-of-hooks */
import styled from '@emotion/styled'
import { css } from '@emotion/react'
import { Divider, FormControlLabel, Typography } from '@mui/material'
import { Colors, Shapes, Typographies, printStyle, useTypography } from 'src/components/theme'
import { AntSwitch } from 'src/components/switch'
import { PropsWithChildren, Suspense } from 'react'
import { Loading } from 'src/components/insights/custom-polygon-visits/loading'
import { ErrorBoundary } from 'src/components/errors/error-boundary'
import { NO_PRINT_REPORTS } from 'src/utils/html-to-image'
import { INSIGHT_WIDGET_LOCKED_CLASS, INSIGHT_WIDGET_PRINTABLE_CLASS } from 'src/atoms/report-insight'
import { LockedFeature } from 'src/components/locked-feature'
import { ButtonWithIcon } from 'src/components/button/button-with-icon'
import { rgbaStrOpacity } from 'src/utils/colors'
import { COMMANDAI_IDS_MAP } from 'src/constants/command-ai-ids'

const cardWrapperPrintStyle = printStyle`
  break-inside: avoid;
`
const CardWrapper = styled.div<{ advanced?: boolean; color: string }>`
  position: relative;
  border-radius: ${Shapes.borderRadius}px;
  background-color: ${Colors.bgWhite};
  box-shadow: ${Colors.BOX_SHADOW};
  overflow: hidden;
  ${cardWrapperPrintStyle}
`
type CardSectionProps = {
  bgColor: string
  color: string
  advanced?: boolean
  gradientOpacity?: 'light' | 'medium' | 'dark'
}
// Card Header
const CardHeader = styled.div<CardSectionProps>`
  position: relative;
  display: flex;
  justify-content: space-between;
  align-items: center;
  min-height: 36px;
  padding: ${Shapes.spacing[0]}px ${Shapes.spacing[4]}px;
  border-style: solid;

  ${({ bgColor, advanced, gradientOpacity }) =>
    advanced
      ? css`
          border-width: 2px;
          border-color: ${Colors.redDark};
          background-color: ${Colors.redDark};
        `
      : css`
          border-width: 0px;
          border-color: transparent;
          background-image: linear-gradient(
              90deg,
              rgba(255, 255, 255, 0.2),
              rgba(0, 0, 0, ${gradientOpacity === 'light' ? 0.1 : gradientOpacity === 'medium' ? 0.2 : 0.3})
            ),
            linear-gradient(${bgColor}, ${bgColor});
        `}
  color: ${(props) => (props.advanced ? Colors.textWhite : props.color)};
  & > .MuiTypography-root:first-of-type:not(:only-child) {
    margin-top: ${Shapes.spacing[3]}px;
  }
`
const CardHeaderText = styled.div<{ advanced?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: ${Shapes.spacing[1]}px;
  padding-block: ${Shapes.spacing[1]}px;

  & > .MuiButtonBase-root {
    background-color: transparent;
    color: ${Colors.textWhite};
  }
  & > p {
    margin: 0;
  }
`
const CardHeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: ${Shapes.spacing[1]}px;

  .button-with-icon {
    padding: ${Shapes.spacing[1]}px;
    background-color: transparent;
    color: ${Colors.textWhite};

    &:hover {
      background-color: transparent;
      border-color: ${Colors.bgWhite};
    }
    &:disabled {
      background-color: transparent;
    }
  }
`
const StyledFormControlLabel = styled(FormControlLabel)`
  border: 1px solid ${(props) => (props.checked ? Colors.textWhite : 'transparent')};
  border-radius: ${Shapes.borderRadius}px;
  background-color: transparent;
  padding: ${Shapes.spacing[1]}px ${Shapes.spacing[2]}px;
  margin: 0;
  .MuiFormControlLabel-label {
    ${useTypography(Typographies.textmd)}
    color: ${Colors.textWhite};
    margin-left: ${Shapes.spacing[1]}px;
  }
  .MuiSwitch-track {
    border: 1px solid rgba(255, 255, 255, 60%);
  }
`

// Border logic for card body
const borderStyle = (bgColor: string, advanced: boolean) =>
  advanced
    ? css`
        border-left: 2px solid ${Colors.redDark};
        border-right: 2px solid ${Colors.redDark};
        border-bottom: 2px solid ${Colors.redDark};
      `
    : css`
        border-left: 1px solid ${Colors.border};
        border-right: 1px solid ${Colors.border};
        border-bottom: 1px solid ${Colors.border};
      `
const CardBody = styled.div<CardSectionProps>`
  display: flex;
  flex-direction: column;
  gap: ${Shapes.spacing[4]}px;
  background-color: ${Colors.bgWhite};
  border-bottom-left-radius: ${Shapes.borderRadius}px;
  border-bottom-right-radius: ${Shapes.borderRadius}px;
  ${(props) => borderStyle(props.bgColor, !!props.advanced)}
  overflow: visible;
`

// Card Info
const CardInfo = styled.div`
  padding: ${Shapes.spacing[0]}px ${Shapes.spacing[4]}px ${Shapes.spacing[4]}px ${Shapes.spacing[4]}px;
`

// Card Filters
export const CardFilters = styled.div`
  display: flex;
  flex-direction: column;
  background-color: ${Colors.bgWhite};
  padding-top: ${Shapes.spacing[4]}px;
  padding-inline: ${Shapes.spacing[4]}px;
  & > div:nth-of-type(2) {
    padding-top: 0;
  }
`
export const CardFiltersContent = styled.div<{ advanced?: boolean }>`
  width: 100%;
  padding: ${Shapes.spacing[4]}px;
  background-color: ${Colors.bgMedium};
  border-radius: ${Shapes.borderRadius}px;
  border: 1px solid ${Colors.border};
`
const CardFiltersContentAdvanced = styled.div`
  padding-top: ${Shapes.spacing[4]}px;
`

const CardFiltersContentFallback = styled.div`
  height: 53px;
`

// Card Chart
const CardChart = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${Shapes.spacing[4]}px;
  padding: ${Shapes.spacing[4]}px 0;
  background-color: ${Colors.bgWhite};
`
const CardStats = styled.div`
  &:empty {
    display: none;
  }
`
const ChartContainer = styled.div`
  width: 100%;
  z-index: 1;
`
const ChartFooter = styled.div`
  padding-inline: ${Shapes.spacing[4]}px;
  &:empty {
    display: none;
  }
`

// Card Footer
const CardFooter = styled.div<{ advanced?: boolean }>`
  border-bottom-left-radius: ${Shapes.borderRadius}px;
  border-bottom-right-radius: ${Shapes.borderRadius}px;
  background-color: ${Colors.bgWhite};
`

const Subtitle = styled(Typography)`
  opacity: 0.9;
  margin-bottom: ${Shapes.spacing[3]}px;
`

type WidgetCardProps = {
  id?: string
  // Header props
  title: string
  subTitle?: string
  compare?: React.ReactNode
  actions?: React.ReactNode
  headerTextColor?: string
  headerBgColor?: string
  headerGradientOpacity?: 'light' | 'medium' | 'dark'
  // Info
  info?: React.ReactNode
  // Filters
  filters?: React.ReactNode
  advancedFilters?: React.ReactNode
  hideFiltersWhenPrint?: boolean
  // Sections
  sections?: Array<{
    header?: React.ReactNode
    content?: React.ReactNode
    footer?: React.ReactNode
  }>
  // Footer
  footer?: React.ReactNode
  // Advanced version
  hasAdvancedMode?: boolean
  advanced?: boolean
  onAdvancedChange?: (value: boolean) => void
  loaderHeight?: number
  locked?: boolean
  disablePrintable?: boolean
}

const ChartLoading = styled.div`
  & > div:first-of-type {
    border-radius: ${Shapes.borderRadius}px;
  }
`

export const WidgetCard = (props: WidgetCardProps) => {
  const {
    id,
    hasAdvancedMode = false,
    advanced,
    onAdvancedChange,
    title,
    subTitle,
    compare,
    actions,
    headerBgColor = Colors.bgWhite,
    headerTextColor = Colors.textBlack,
    headerGradientOpacity = 'medium',
    info,
    filters,
    advancedFilters,
    hideFiltersWhenPrint,
    locked,
    sections,
    footer,
    loaderHeight,
    disablePrintable,
  } = props
  const className = disablePrintable ? '' : locked ? INSIGHT_WIDGET_LOCKED_CLASS : INSIGHT_WIDGET_PRINTABLE_CLASS

  return (
    <ErrorBoundary>
      <CardWrapper id={id} className={className} advanced={advanced} color={headerBgColor}>
        <CardHeader
          advanced={advanced}
          color={headerTextColor}
          bgColor={headerBgColor}
          gradientOpacity={headerGradientOpacity}>
          <CardHeaderText>
            <Typography variant="title1" fontSize={16}>
              {title}
            </Typography>
            {subTitle && (
              <Subtitle variant="title2m" fontSize={12}>
                {subTitle}
              </Subtitle>
            )}
          </CardHeaderText>

          {!locked && (
            <CardHeaderActions className={NO_PRINT_REPORTS}>
              {hasAdvancedMode && (
                <>
                  <StyledFormControlLabel
                    color={headerBgColor}
                    checked={advanced}
                    control={
                      <AntSwitch bgcolor={advanced ? Colors.redDark : headerBgColor} onChange={onAdvancedChange} />
                    }
                    label="Advanced"
                    id={COMMANDAI_IDS_MAP.mapExplorer['advanced-filters-toggle']}
                  />
                  <Divider
                    style={{
                      borderColor: `${rgbaStrOpacity(Colors.textWhite, 0.3)}`,
                      marginInline: `${Shapes.spacing[1]}px`,
                    }}
                    orientation="vertical"
                    flexItem
                  />
                </>
              )}
              {compare}
              {actions && (
                <>
                  <Typography variant="textmdm">Export:</Typography>
                  {actions}
                </>
              )}
            </CardHeaderActions>
          )}
        </CardHeader>

        <CardContent locked={locked}>
          <CardBody bgColor={headerBgColor} color={headerTextColor} advanced={advanced}>
            {info && <CardInfo>{info}</CardInfo>}

            {filters && (
              <CardFilters className={hideFiltersWhenPrint ? NO_PRINT_REPORTS : ''}>
                <CardFiltersContent>
                  <Suspense fallback={<CardFiltersContentFallback />}>
                    {filters}
                    {advanced && advancedFilters && (
                      <CardFiltersContentAdvanced>{advancedFilters}</CardFiltersContentAdvanced>
                    )}
                  </Suspense>
                </CardFiltersContent>
              </CardFilters>
            )}
            {sections &&
              sections.map((section, index) => {
                const containerKey = `container-${index}-${title}`
                const chartKey = `chart-${index}-${title}`
                const legendKey = `legend-${index}-${title}`
                return (
                  <Suspense
                    key={containerKey}
                    fallback={
                      <ChartLoading>
                        <Loading hideLabel height={loaderHeight} />
                      </ChartLoading>
                    }>
                    <CardChart id={containerKey}>
                      {section.header && <CardStats>{section.header}</CardStats>}
                      <Suspense
                        key={chartKey}
                        fallback={
                          <ChartLoading>
                            <Loading hideLabel height={loaderHeight} />
                          </ChartLoading>
                        }>
                        <ChartContainer>{section.content}</ChartContainer>
                      </Suspense>
                      {section.footer && (
                        <Suspense
                          key={legendKey}
                          fallback={
                            <ChartLoading>
                              <Loading hideLabel height={loaderHeight} />
                            </ChartLoading>
                          }>
                          <ChartFooter>{section.footer}</ChartFooter>
                        </Suspense>
                      )}
                    </CardChart>
                  </Suspense>
                )
              })}
            {footer && (
              <Suspense fallback={null}>
                <CardFooter>{footer}</CardFooter>
              </Suspense>
            )}
          </CardBody>
        </CardContent>
      </CardWrapper>
    </ErrorBoundary>
  )
}

type CardContentProps = PropsWithChildren<{
  locked?: boolean
}>

const CardContent = ({ locked, children }: CardContentProps) => {
  if (locked) {
    return <LockedCardContent />
  }
  return <>{children}</>
}

const LockedContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: ${Shapes.spacing[5]}px;
  margin: ${Shapes.spacing[4]}px;
  padding: ${Shapes.spacing[6]}px ${Shapes.spacing[4]}px;
  background-color: rgba(239, 239, 239, 1);
  border-radius: ${Shapes.borderRadius}px;
`

const LockedIconWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: ${Shapes.spacing[6]}px;
  height: ${Shapes.spacing[6]}px;
  background-color: ${Colors.bgWhite};
  border: solid 2px ${Colors.primaryYellow400};
  border-radius: 100%;
`

const ButtonGroup = styled.div`
  display: flex;
  gap: ${Shapes.spacing[4]}px;
  button {
    height: 40px;
    width: 220px;
  }
`

const LockedCardContent = () => {
  return (
    <LockedContentContainer>
      <LockedIconWrapper>
        <LockedFeature isLocked />
      </LockedIconWrapper>
      <Typography fontSize={Shapes.spacing[4]} color={Colors.textGreyBlue}>
        This widget isn't included in your current plan.
      </Typography>
      <ButtonGroup>
        <ButtonWithIcon inverted>Upgrade Plan</ButtonWithIcon>
        <ButtonWithIcon>View in a Sample Location</ButtonWithIcon>
      </ButtonGroup>
    </LockedContentContainer>
  )
}
